/**
 * Country-agnostic model for the emblem that sits between a plate's region code and its number.
 *
 * Austria puts the arms of the Bundesland there, Germany the arms of the Land plus a seal,
 * Switzerland the canton's arms, Slovenia the municipal coat of arms. The shape of the problem is
 * the same in every case — *map a region code to an emblem* — so it is modelled once here and the
 * per-country data lives in its own file (`at-districts.ts`, and later `de-districts.ts` …).
 *
 * ## Adding a country
 *
 *  1. Create `xx-districts.ts` exporting a {@link PlateRegionSet}.
 *  2. Attach it to that country's entry in `PLATE_COUNTRIES` as `regions`.
 *
 * Nothing in `PlateComponent` changes: it resolves emblems through {@link findRegionEmblem}, which
 * knows nothing about any particular country.
 */

/** The emblem drawn between region code and number. */
export interface CoatOfArms {
  /** Region name in its own language, used as the emblem's accessible label. */
  readonly name: string;
  /**
   * The artwork, as a `data:image/png;base64,…` URI.
   *
   * Inlined rather than fetched so the plate renders with no network — the entry/exit/payment
   * displays have to draw plates while the uplink is down. See `at-arms.ts`.
   */
  readonly src: string;
  /** Width ÷ height of the artwork. Real arms are not square, and squashing them looks wrong. */
  readonly ratio: number;
}

/** One region a country issues plates for. */
export interface PlateRegion {
  /** District/city name, e.g. "Linz-Land". */
  readonly name: string;
  /**
   * Key into {@link PlateRegionSet.emblems}. Absent for regions that carry no regional arms —
   * Austria's special series (police, military, diplomatic corps) are valid codes belonging to no
   * Bundesland.
   */
  readonly emblem?: string;
  /**
   * Longest number permitted after the emblem. Austria's spreadsheet calls this "Max. characters
   * after coat of arms": 6 for the single-letter city codes (W, L, G, I, K, S, E, P, B), 5 for
   * everything else.
   */
  readonly maxChars?: number;
}

/**
 * One country's region data: which region codes exist, and what emblem each one carries.
 *
 * Emblems are held separately from regions because many regions share one — all 18 Upper Austrian
 * districts carry the same Bundesland arms, and inlining the artwork per district would repeat a
 * 6 kB image 18 times.
 */
export interface PlateRegionSet {
  /** Region code (uppercase, no separators) → that region's data. */
  readonly districts: Readonly<Record<string, PlateRegion>>;
  /** Emblem key → the arms to draw. */
  readonly emblems: Readonly<Record<string, CoatOfArms>>;
}

/** A region's data, or `undefined` when the code is not one this country issues. */
export function findRegion(
  regions: PlateRegionSet | undefined,
  regionCode: string | null | undefined
): PlateRegion | undefined {
  const code = regionCode?.trim().toUpperCase();
  return regions && code ? regions.districts[code] : undefined;
}

/**
 * Whether `regionCode` is a region this country actually issues.
 *
 * True for Austria's special series too, which are real codes that simply carry no regional arms —
 * so this is *not* the same as "has an emblem".
 */
export function isKnownRegion(
  regions: PlateRegionSet | undefined,
  regionCode: string | null | undefined
): boolean {
  return !!findRegion(regions, regionCode);
}

/**
 * The emblem for a region code, or `undefined` when the country has no region data, the code is
 * unknown, or the region legitimately carries no arms.
 *
 * Callers must treat `undefined` as "draw no emblem", never as "reject the plate".
 */
export function findRegionEmblem(
  regions: PlateRegionSet | undefined,
  regionCode: string | null | undefined
): CoatOfArms | undefined {
  const emblem = findRegion(regions, regionCode)?.emblem;
  return emblem ? regions!.emblems[emblem] : undefined;
}
